/**
 * EF-CREW-OVERLAP-PREVENTION-V1: Shift Conflict Dialog
 * 
 * Shows detailed conflict information when attempting to assign a crew member
 * to a shift that overlaps with their existing assignments.
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { AlertTriangle, Clock, Calendar, User, X } from 'lucide-react';

/**
 * ShiftConflictDialog - Shows overlap conflicts and allows admin override
 * 
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Callback to close the dialog
 * @param {object} conflictData - Conflict data from the backend
 * @param {string} crewName - Name of the crew member being assigned
 * @param {function} onOverride - Callback when admin chooses to override (receives reason)
 * @param {boolean} isSubmitting - Whether override is being submitted
 */
export const ShiftConflictDialog = ({
  isOpen,
  onClose,
  conflictData,
  crewName,
  onOverride,
  isSubmitting = false,
}) => {
  const [overrideReason, setOverrideReason] = React.useState('');
  const [showOverrideForm, setShowOverrideForm] = React.useState(false);

  const conflicts = conflictData?.conflicts || [];
  const totalOverlapMinutes = conflictData?.total_overlap_minutes || 0;
  const canOverride = conflictData?.can_override !== false;

  const handleOverrideClick = () => {
    if (overrideReason.trim()) {
      onOverride(overrideReason.trim());
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    // Handle both "2026-03-25 14:00" and ISO formats
    const parts = timeStr.split(' ');
    if (parts.length === 2) {
      return parts[1];
    }
    return timeStr.slice(11, 16); // Extract HH:MM from ISO
  };

  const formatOverlapDuration = (minutes) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hours`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="shift-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Scheduling Conflict Detected
          </DialogTitle>
          <DialogDescription>
            {crewName} cannot be assigned to this shift due to overlapping assignments.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-destructive">
              <Clock className="h-4 w-4" />
              {conflicts.length} overlapping assignment{conflicts.length > 1 ? 's' : ''}
              {totalOverlapMinutes > 0 && (
                <span className="text-xs text-muted-foreground">
                  ({formatOverlapDuration(totalOverlapMinutes)} total)
                </span>
              )}
            </div>
          </div>

          {/* Conflict list */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {conflicts.map((conflict, index) => (
              <div 
                key={index} 
                className="rounded-md border bg-muted/30 p-3"
                data-testid={`conflict-item-${index}`}
              >
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {conflict.conflicting_shift_name || 'Existing Shift'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {conflict.conflicting_shift_date}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatTime(conflict.other_effective_start)} - {formatTime(conflict.other_effective_end)}
                    </p>
                    {conflict.overlap_minutes && (
                      <p className="text-xs text-destructive mt-1">
                        Overlap: {formatOverlapDuration(conflict.overlap_minutes)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Override section */}
          {canOverride && (
            <div className="border-t pt-4">
              {!showOverrideForm ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOverrideForm(true)}
                  className="w-full"
                  data-testid="show-override-form-btn"
                >
                  I understand the conflict - allow override
                </Button>
              ) : (
                <div className="space-y-3">
                  <Label htmlFor="override-reason" className="text-sm font-medium">
                    Override Reason (Required)
                  </Label>
                  <Textarea
                    id="override-reason"
                    placeholder="Enter reason for allowing this conflicting assignment..."
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="h-20"
                    data-testid="override-reason-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This override will be logged for audit purposes.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="conflict-dialog-cancel"
          >
            Cancel
          </Button>
          {showOverrideForm && canOverride && (
            <Button
              variant="destructive"
              onClick={handleOverrideClick}
              disabled={!overrideReason.trim() || isSubmitting}
              data-testid="conflict-dialog-override"
            >
              {isSubmitting ? 'Assigning...' : 'Override & Assign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * BulkConflictDialog - Shows conflicts for bulk crew assignments
 */
export const BulkConflictDialog = ({
  isOpen,
  onClose,
  conflictingCrew,
  onOverride,
  isSubmitting = false,
}) => {
  const [overrideReason, setOverrideReason] = React.useState('');
  const [showOverrideForm, setShowOverrideForm] = React.useState(false);

  const handleOverrideClick = () => {
    if (overrideReason.trim()) {
      onOverride(overrideReason.trim());
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="bulk-conflict-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Multiple Scheduling Conflicts
          </DialogTitle>
          <DialogDescription>
            {conflictingCrew?.length || 0} crew member(s) have overlapping shift assignments.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Conflict list */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {conflictingCrew?.map((crew, index) => (
              <div 
                key={index} 
                className="rounded-md border bg-muted/30 p-3"
                data-testid={`bulk-conflict-crew-${index}`}
              >
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">
                      {crew.staff_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {crew.conflicts?.length || 0} overlapping shift(s)
                    </p>
                    {crew.total_overlap_minutes > 0 && (
                      <p className="text-xs text-destructive">
                        Total overlap: {crew.total_overlap_minutes} minutes
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Override section */}
          <div className="border-t pt-4">
            {!showOverrideForm ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowOverrideForm(true)}
                className="w-full"
                data-testid="bulk-show-override-form-btn"
              >
                I understand - allow override for all
              </Button>
            ) : (
              <div className="space-y-3">
                <Label htmlFor="bulk-override-reason" className="text-sm font-medium">
                  Override Reason (Required)
                </Label>
                <Textarea
                  id="bulk-override-reason"
                  placeholder="Enter reason for allowing these conflicting assignments..."
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  className="h-20"
                  data-testid="bulk-override-reason-input"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="bulk-conflict-dialog-cancel"
          >
            Cancel
          </Button>
          {showOverrideForm && (
            <Button
              variant="destructive"
              onClick={handleOverrideClick}
              disabled={!overrideReason.trim() || isSubmitting}
              data-testid="bulk-conflict-dialog-override"
            >
              {isSubmitting ? 'Assigning...' : 'Override All & Assign'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftConflictDialog;
